import { BookmarkBorder, LocationOn, LocationOnOutlined } from '@mui/icons-material';
import { Divider } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ReactComponent as BedOutlined } from 'assets/svg/bedrooms.svg';
import { ReactComponent as BathroomsOutlined } from 'assets/svg/feature-bathroom.svg';
import { ReactComponent as Budget } from 'assets/svg/budget.svg';
import { ReactComponent as ConstructedArea } from 'assets/svg/constructed-area.svg';
import { ReactComponent as DefaultProperty } from 'assets/svg/default-property.svg';
import { ReactComponent as DefaultPropertyBlue } from 'assets/svg/default-property-blue.svg';
import { ReactComponent as Commission } from 'assets/svg/percentage-square.svg';
import { ReactComponent as Urgency } from 'assets/svg/urgency.svg';
import { useSavedOrRemovePropertyRequest } from 'hooks/usePropertyRequests';
import useAddRequestStore from 'store/addRequestStore';
import usePropertyRequestsStore from 'store/propertyRequestsStore';
import { PropertyLead } from 'types/PropertyLead';
import { formatDateForPropertyRequests } from 'util/formatDate';
import { urgencyOptions } from 'util/urgencySelector';

export default function PropertyCard({ lead }: { lead: PropertyLead }) {
    const { setPropertyRequestDetailModalVisible } = useAddRequestStore();
    const { setSelectedLead, savedRequestsPage } = usePropertyRequestsStore();
    const { mutate: savePropertyRequest } = useSavedOrRemovePropertyRequest();

    const savedRequests = useQueryClient().getQueryData(['savedPropertyRequests', savedRequestsPage]) as Record<string, any>;

    const isSaved = savedRequests?.data?.data?.leads?.some((request: Record<string, any>) => request.id === lead.id);

    return (
        <div className="w-full lg:max-w-3xl font-poppins  rounded-xl shadow-sm  space-y-6 border border-gray-200 h-full flex flex-col justify-between ">
            <div>
                <div
                    className="bg-my-gray/10 space-y-1 xs:block flex xs:flex-col flex-row md:items-center gap-6 p-6 border-b border-gray-200 cursor-pointer"
                    onClick={() => {
                        setPropertyRequestDetailModalVisible(true);
                        setSelectedLead(lead);
                    }}
                >
                    <div className="w-14 h-14 bg-my-blue/10 rounded-lg flex items-center justify-center">
                        <DefaultPropertyBlue className="w-7 h-7" />
                    </div>
                    <div className="flex flex-col gap-2">
                        <h3 className="text-lg text-my-black/80 font-semibold md:mt-0 mt-2">{lead.title}</h3>
                        <h4 className="text-[12px] text-my-black/50 ">
                            {lead.definedArea ? lead.definedArea : lead.cities?.map((city) => city.city).join(', ')} • {lead.propertyType}
                        </h4>
                    </div>
                </div>

                <div className="space-y-4 px-6 pt-4">
                    {lead.definedArea ? (
                        <Details title="Area" value={lead.definedArea} icon={<LocationOnOutlined className="h-6 w-6 text-gray-500" />} />
                    ) : (
                        <>
                            <Details title="Country" value={lead.propertyCountry} icon={<LocationOnOutlined className="h-6 w-6 text-gray-500" />} />
                            <Details
                                title="State"
                                value={lead.states?.map((state) => state.state).join(', ')}
                                icon={<LocationOnOutlined className="h-6 w-6 text-gray-500" />}
                            />
                            {lead.propertyNeighborhood && lead.propertyNeighborhood.length > 0 && (
                                <Details
                                    title="Neighborhood"
                                    value={lead.propertyNeighborhood.join(', ')}
                                    icon={<LocationOnOutlined className="h-6 w-6 text-gray-500" />}
                                />
                            )}
                        </>
                    )}
                    {lead.minBudget !== null && lead.maxBudget !== null && (
                        <Details title="Budget" value={`€${lead.minBudget} - €${lead.maxBudget}`} icon={<Budget className="h-6 w-6 text-gray-500" />} />
                    )}
                    {lead.minConstructionArea !== null && lead.maxConstructionArea !== null && (
                        <Details
                            title="Constructed area"
                            value={`${lead.minConstructionArea} - ${lead.maxConstructionArea}`}
                            icon={<ConstructedArea className="h-6 w-6 text-gray-500" />}
                        />
                    )}
                    {lead.minPlotArea !== null && lead.maxPlotArea !== null && (
                        <Details
                            title="Plot area"
                            value={`${lead.minPlotArea} - ${lead.maxPlotArea}`}
                            icon={<ConstructedArea className="h-6 w-6 text-gray-500" />}
                        />
                    )}
                    {lead.minBedrooms !== null && lead.maxBedrooms !== null && (
                        <Details
                            title="Approx. Bedrooms"
                            value={`${lead.minBedrooms}-${lead.maxBedrooms}`}
                            icon={<BedOutlined className="h-6 w-6 text-gray-500" />}
                        />
                    )}
                    {lead.minBathrooms !== null && lead.maxBathrooms !== null && (
                        <Details
                            title="Approx. Bathrooms"
                            value={`${lead.minBathrooms}-${lead.maxBathrooms}`}
                            icon={<BathroomsOutlined className="h-6 w-6 text-gray-500" />}
                        />
                    )}
                    <Details
                        title="Urgency"
                        value={urgencyOptions.find((option) => option.value === lead.urgency)?.label!}
                        icon={<Urgency className="h-6 w-6 text-gray-500" />}
                    />
                    <Details title="Offered Commission" value={`${lead.commission}%`} icon={<Commission className="h-6 w-6 text-gray-500" />} />
                    {lead.buyerProfile && (
                        <Details title="Buyer Profile" value={lead.buyerProfile} icon={<DefaultProperty className="h-6 w-6 text-gray-500" />} />
                    )}
                </div>
            </div>
            <div>
                <Divider />
                <div className="mt-4 px-6 pb-5 flex items-center justify-between">
                    <p className="text-base text-gray-500">Posted {formatDateForPropertyRequests(lead.createdAt)}</p>

                    <div
                        className={`p-2   ${isSaved ? 'bg-my-green rounded-lg' : 'bg-gray-50 rounded'}`}
                        onClick={() => savePropertyRequest({ propertyLeadId: lead.id, action: isSaved ? 'remove' : 'save' })}
                    >
                        <BookmarkBorder className={` cursor-pointer ${isSaved ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                </div>
            </div>
        </div>
    );
}

const Details = ({ title, value, icon }: { title: string; value: string; icon: React.ReactNode }) => {
    return (
        <div className="flex items-center gap-3">
            <span className="xs:hidden block">{icon}</span>
            <div className="space-y-0.5 flex xs:flex-col flex-row  w-full justify-between">
                <p className="text-[12px] flex-1 font-medium flex items-center gap-2 my-2 text-light-black/80">
                    <span className="xs:block hidden">{icon}</span>
                    {title}
                </p>
                <h2 className="text-sm flex-1  md:p-3 text-my-black/70 font-normal ">{value}</h2>
            </div>
        </div>
    );
};
